import { useEffect, useState } from "react";
import { Loader2, Save, RotateCcw, Eye, Trash2 } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { SCHEMAS, withDefaults } from "@/lib/sections";
import { FieldInput } from "./FieldInput";
import { PreviewDialog } from "./PreviewDialog";

const clean = (content) =>
  Object.fromEntries(
    Object.entries(content).map(([k, v]) => [
      k,
      Array.isArray(v) && v.every((x) => typeof x === "string") ? v.filter((x) => x.trim() !== "") : v,
    ])
  );

export const SectionEditor = ({ section, onClose, onSave, onDelete }) => {
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(false);
  const schema = section ? SCHEMAS[section.type] : null;

  useEffect(() => {
    if (section) setDraft(withDefaults(section.type, section.content));
  }, [section]);

  const save = async () => {
    setSaving(true);
    const cleaned = clean(draft);
    const isDefault = JSON.stringify(cleaned) === JSON.stringify(clean({ ...schema.defaults }));
    await onSave(section.id, isDefault && section.builtin ? {} : cleaned);
    setSaving(false);
  };

  return (
    <Sheet open={!!section} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full overflow-y-auto border-slate-800 bg-slate-900 text-white sm:max-w-xl" data-testid="section-editor">
        <SheetHeader>
          <SheetTitle className="text-white">{schema?.label}</SheetTitle>
        </SheetHeader>
        {schema && (
          <div className="mt-6 space-y-5">
            {schema.fields.length === 0 && (
              <p className="text-sm text-slate-400">Esta sección no tiene textos editables; puedes moverla u ocultarla desde la lista.</p>
            )}
            {schema.fields.map((f) => (
              <FieldInput key={f.key} field={f} value={draft[f.key]} onChange={(v) => setDraft((d) => ({ ...d, [f.key]: v }))} testId={`field-${f.key}`} />
            ))}
            <div className="sticky bottom-0 -mx-6 flex flex-wrap gap-2 border-t border-slate-800 bg-slate-900 px-6 py-4">
              <button onClick={save} disabled={saving} className="admin-btn-primary" data-testid="section-save-btn">
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Guardar
              </button>
              <button onClick={() => setPreview(true)} className="admin-btn" data-testid="section-preview-btn">
                <Eye size={14} /> Vista previa
              </button>
              <button onClick={() => setDraft({ ...schema.defaults })} className="admin-btn" data-testid="section-reset-btn">
                <RotateCcw size={14} /> Restaurar
              </button>
              {!section.builtin && (
                <button onClick={() => onDelete(section)} className="admin-btn ml-auto hover:border-red-500/50 hover:text-red-400" data-testid="section-editor-delete-btn">
                  <Trash2 size={14} /> Eliminar
                </button>
              )}
            </div>
          </div>
        )}
        <PreviewDialog open={preview} onOpenChange={setPreview} section={section} content={clean(draft)} />
      </SheetContent>
    </Sheet>
  );
};

export default SectionEditor;
